import { Box, Button, Flex, Heading, IconButton, Link, Skeleton, useDisclosure } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'react-use';

import BookmarkIcon from '@/assets/icons/ic-bookmark.svg';
import CopyIcon from '@/assets/icons/ic-copy.svg';
import QuoteIcon from '@/assets/icons/ic-quote.svg';
import ShareIcon from '@/assets/icons/ic-share.svg';

import { BookmarkLectureModal } from '@/components/ui/features/lecture/BookmarkLectureModal/BookmarkLectureModal';
import { CiteLectureModal } from '@/components/ui/features/lecture/CiteLectureModal/CiteLectureModal';
import { useLecturePageQueryParams } from '@/components/ui/features/lecture/hooks';
import { getLectureVariant } from '@/components/ui/features/lecture/LectureHeaderSection/LectureHeaderSection.utils';
import { AuthorsList } from '@/components/ui/features/lecture/LectureInfo/components/AuthorsList';
import { EmbedButton } from '@/components/ui/features/lecture/LectureInfo/components/EmbedButton';
import { LectureInfoSubtitle } from '@/components/ui/features/lecture/LectureInfo/components/LectureInfoSubtitle';
import { LabelText } from '@/components/ui/features/lecture/LectureInfo/LectureInfo.elements';
import { ShareLectureModal } from '@/components/ui/features/lecture/ShareLectureModal/ShareLectureModal';
import { LectureCardAwardTooltip } from '@/components/ui/shared/lecture/LectureAwardTooltip/LectureCardAwardTooltip';
import { LogInPopup } from '@/components/ui/shared/modals/LoginPopup/LogInPopup';
import { useAnalytics } from '@/context/Analytics';
import { LectureVariants } from '@/enums/LectureVariants';
import { IToastType } from '@/enums/ToastType';
import { queryThinLecture } from '@/fetchers/thinLectures';
import { useToast } from '@/hooks/useToast';
import { useResource } from '@/lib/datx-jsonapi-react';
import { sessionQueryOptions } from '@/queries/upgrade/sessions';

const DOI_BASE_URL = 'https://doi.org/';

export const LectureInfo: FC = () => {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { data: session } = useQuery(sessionQueryOptions());
  const user = session?.user;
  const [clipboardState, copyToClipboard] = useCopyToClipboard();
  const toast = useToast();

  const { id: lectureId } = useLecturePageQueryParams();

  const { isOpen: isCiteOpen, onOpen: onCiteOpen, onClose: onCiteClose } = useDisclosure();
  const { isOpen: isShareOpen, onOpen: onShareOpen, onClose: onShareClose } = useDisclosure();
  const { isOpen: isBookmarkOpen, onOpen: onBookmarkOpen, onClose: onBookmarkClose } = useDisclosure();
  const { isOpen: isLoginOpen, onOpen: onLoginOpen, onClose: onLoginClose } = useDisclosure();

  const { data: lecture } = useResource(queryThinLecture(lectureId, ['tag', 'event', 'position_sorted_profiles']));
  const lectureVariant = useMemo(() => getLectureVariant(lecture), [lecture]);

  const showEmbedButton = lectureVariant === LectureVariants.Full || lectureVariant === LectureVariants.Video;

  const shareOpenHandler = () => {
    onShareOpen();
    analytics.track('library_lecture_share', {
      event_id: lecture.eventId,
      lecture_name: lecture.title,
      speaker_name: lecture.sortedProfiles.map((speaker) => speaker.fullName),
      lecture_id: lecture.id,
    });
  };

  const citeOpenHandler = () => {
    onCiteOpen();
    analytics.track('library_lecture_cite', {
      event_id: lecture.eventId,
      lecture_name: lecture.title,
      speaker_name: lecture.sortedProfiles.map((speaker) => speaker.fullName),
      lecture_id: lecture.id,
    });
  };

  function handleOpenBookmark() {
    if (user) {
      onBookmarkOpen();
      return;
    }
    onLoginOpen();
  }

  const onVideoDoiCopy = () => {
    copyToClipboard(DOI_BASE_URL + lecture?.underlineDoi);

    if (clipboardState.error) {
      toast({ status: IToastType.ERROR, title: t('newLecture.copyError') });
    } else {
      toast({ status: IToastType.SUCCESS, title: t('newLecture.copySuccess') });
    }
  };

  return (
    <Flex justifyContent="space-between" flexDirection="column-reverse" color="white">
      <Flex gap="4" flexDirection={{ base: 'column', lg: 'row' }} mt="4">
        {lecture?.underlineDoi && (
          <Box>
            {t('newLecture.underlineDoi')}: {DOI_BASE_URL + lecture?.underlineDoi}
            <IconButton
              ml="2"
              w="6"
              h="6"
              onClick={onVideoDoiCopy}
              aria-label="copy doi"
              variant="unstyled"
              icon={<CopyIcon width="24px" h="24px" />}
            />
          </Box>
        )}
        {lecture?.doi && (
          <Box>
            {t('newLecture.paperDoi')}:{' '}
            <Link target="_blank" rel="noopener noreferrer" href={lecture?.doi}>
              {lecture?.doi}
            </Link>
          </Box>
        )}
      </Flex>
      <AuthorsList sortedProfiles={lecture?.positionSortedProfiles} />
      <Box mt={4}>
        <Flex mb={3} flexDirection={['column', null, null, 'row']}>
          {/* TODO: Move to LectureInfoSubtitle */}
          {lecture?.tag?.name ? (
            <LabelText textTransform="uppercase" mr={6}>
              {lecture?.tag?.name}
            </LabelText>
          ) : (
            <Skeleton height="1.2rem" width="150px" mr={6} />
          )}
          {<LectureInfoSubtitle />}
        </Flex>
        <Box>
          {!lecture && <Skeleton height="1.6rem" width="300px" mt={4} />}
          {lecture && (
            <Flex gap={2} alignItems="flex-start">
              <Box h={{ base: '2rem', md: '2.5rem', lg: '3rem' }} display="flex" alignItems="center" flexShrink={0}>
                <LectureCardAwardTooltip
                  award={lecture.awards}
                  iconWidth={{ base: '18px', lg: '24px' }}
                  iconColor="white"
                />
              </Box>
              <Heading
                fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                lineHeight={{ base: '2rem', md: '2.5rem', lg: '3rem' }}
              >
                {lecture?.title}
              </Heading>
            </Flex>
          )}
        </Box>
      </Box>
      {lecture ? (
        <Box
          sx={{
            flexShrink: '0',
            '& svg': {
              width: '24px',
              marginLeft: '5px',
            },
          }}
        >
          <Button type="button" variant="ghost" colorScheme="white" onClick={handleOpenBookmark} pl={0}>
            {t('newLecture.bookmark')}
            <BookmarkIcon css={{ width: '18px', height: '18px' }} />
          </Button>
          <Button type="button" variant="ghost" colorScheme="white" onClick={shareOpenHandler} pl={0}>
            {t('newLecture.share')}
            <ShareIcon />
          </Button>
          <Button type="button" variant="ghost" colorScheme="white" onClick={citeOpenHandler} pl={0}>
            {t('newLecture.cite')}
            <QuoteIcon />
          </Button>
          {showEmbedButton && <EmbedButton />}
        </Box>
      ) : (
        <Flex mt="4">
          <Skeleton height="1.6rem" width="150px" mr={4} />
          <Skeleton height="1.6rem" width="150px" />
        </Flex>
      )}
      <CiteLectureModal lectureId={lectureId} isOpen={isCiteOpen} onClose={onCiteClose} />
      <ShareLectureModal lectureId={lectureId} isOpen={isShareOpen} onClose={onShareClose} />
      <BookmarkLectureModal lectureId={lectureId} isOpen={isBookmarkOpen} onClose={onBookmarkClose} />
      <LogInPopup isOpen={isLoginOpen} onClose={onLoginClose} />
    </Flex>
  );
};
